import { isOSClosed } from './os-status';

/**
 * Como nomear uma OS do bloco "Requer Atenção" — fonte única para o dashboard
 * e para a tela de ação rápida, que mostravam o MESMO número com o MESMO
 * rótulo errado.
 *
 * O rótulo era `Atrasada {hoursStale}h`, e `hoursStale` vem de `updated_at`:
 * a consulta do servidor (`manager-home.ts`) seleciona OS sem movimento há
 * mais de 24h e **nunca olha o prazo**. Ou seja, o app afirmava atraso sem ter
 * verificado nenhum — uma OS combinada para sexta, parada desde ontem, era
 * anunciada na terça como "Atrasada 30h" para um gestor que ia cobrar o
 * mecânico por um prazo que ainda nem chegou.
 *
 * São duas informações diferentes e as duas importam:
 *
 *   - **parada**: ninguém mexe há N horas. É o recorte da lista, e continua
 *     valendo como sinal — carro esquecido no elevador é problema mesmo com
 *     prazo sobrando.
 *   - **atrasada**: o prazo combinado com o cliente passou. Só isso justifica
 *     a palavra.
 *
 * Quando as duas valem, o atraso vem primeiro: é o que o cliente cobra.
 *
 * E as duas só valem para OS **em fluxo ativo**. OS concluída, cancelada ou
 * entregue tem `status` fechado (`OS_CLOSED_STATUSES`) e `updated_at`
 * congelado — o relógio de "parada há Nh" corre para sempre e o prazo fica
 * eternamente no passado. Era o segundo relato de campo pelo mesmo motivo:
 * carro entregue na semana passada anunciado como "Atrasada 6d" na tela do
 * gestor. Ciclo encerrado não atrasa; o `status` entra por isso, e é
 * obrigatório para que nenhuma tela nova volte a afirmar atraso sem olhá-lo.
 */
export interface OSStaleLabel {
  /** Texto curto para o card. */
  text: string;
  /** Prazo vencido de verdade — a tela pinta de alerta só neste caso. */
  overdue: boolean;
  /** Ciclo encerrado: não é parada nem atrasada, e não pede ação. */
  closed: boolean;
}

/** O que dizer no lugar de "parada/atrasada" quando a OS já fechou. */
const CLOSED_TEXT: Record<string, string> = {
  completed: 'Serviço concluído',
  cancelled: 'OS cancelada',
  delivered: 'Veículo entregue',
};

export function osStaleLabel(
  hoursStale: number,
  deadline: string | null,
  status: string | null | undefined,
  now: number = Date.now(),
): OSStaleLabel {
  // Antes de qualquer conta: OS fechada não tem atraso a reportar. Vem
  // primeiro porque tanto o relógio de parada quanto o prazo continuam
  // "vencendo" numa OS encerrada, e os dois mentiriam.
  if (isOSClosed(status)) {
    return {
      text: CLOSED_TEXT[status as string] ?? 'OS encerrada',
      overdue: false,
      closed: true,
    };
  }

  const parada = `Parada há ${hoursStale}h`;

  if (!deadline) return { text: parada, overdue: false, closed: false };

  const dueMs = new Date(deadline).getTime();
  // Prazo ilegível não vira "no prazo" nem "atrasada": some, e sobra o fato
  // que a gente mediu de verdade.
  if (Number.isNaN(dueMs) || dueMs >= now) {
    return { text: parada, overdue: false, closed: false };
  }

  const lateHours = Math.floor((now - dueMs) / (1000 * 60 * 60));
  // Menos de um dia de atraso é mais legível em horas; a partir daí, em dias,
  // porque "Atrasada 76h" ninguém converte de cabeça no balcão.
  const atraso =
    lateHours < 24
      ? `Atrasada ${Math.max(lateHours, 1)}h`
      : `Atrasada ${Math.floor(lateHours / 24)}d`;

  return { text: `${atraso} · parada há ${hoursStale}h`, overdue: true, closed: false };
}

/* ==========================================================================
 * Limiar de "OS parada"
 * ========================================================================== */

/**
 * Horas sem movimento a partir das quais uma OS ativa entra no alerta do
 * gestor — e por que isso NÃO é um número só.
 *
 * O limiar nasceu 24h, que é o certo para mecânica: um carro parado um dia
 * inteiro no elevador é problema. Para funilaria é o oposto — o reparo leva de
 * 5 a 20 dias e o carro fica dias na mesma etapa correndo bem. Com 24h global,
 * TODA OS de funilaria vira "parada" a partir do segundo dia, todo dia, até a
 * entrega. O alerta mais importante do produto vira ruído diário para um
 * segmento inteiro da base — e ruído diário é como o gestor aprende a desligar
 * a notificação, perdendo junto os alertas que importam.
 *
 * Duas camadas, nesta ordem:
 *
 *   1. **Default derivado do segmento declarado.** A oficina que declara
 *      funilaria já chega em 4 dias, sem configurar nada. É o que conserta a
 *      base existente sem depender de ninguém abrir Configurações.
 *   2. **Override do gestor.** Porque a oficina brasileira é híbrida: quem faz
 *      mecânica E funilaria perde sensibilidade nas OS de mecânica com o
 *      default de 4 dias, e precisa poder voltar. O override é a resposta
 *      honesta a esse custo — não um enfeite.
 *
 * A lista de opções é fechada de propósito. O override vem de um jsonb livre
 * (`workshops.settings`), então qualquer coisa pode estar lá: texto, `0`,
 * `null`, um número absurdo. `Number(x) || DEFAULT` não resolve — `"abc"` vira
 * `NaN`, e um limiar `NaN` faz `new Date(now - NaN)` virar Invalid Date, que
 * não filtra nada. Pior ainda seria `0` passar: com limiar zero, **toda** OS
 * ativa é parada. Por isso `resolveStaleOsHours` só aceita valor da lista e
 * cai no default derivado em qualquer outro caso.
 */
export const STALE_OS_HOURS_DEFAULT = 24;

/** Default de quem trabalha em ciclo longo. 4 dias separa funilaria de mecânica que esbarrou num feriado. */
export const STALE_OS_HOURS_LONG_CYCLE = 96;

/** As únicas escolhas válidas: 24h, 48h, 4 dias, 1 semana. */
export const STALE_OS_HOURS_CHOICES = [24, 48, 96, 168] as const;

export type StaleOsHoursChoice = (typeof STALE_OS_HOURS_CHOICES)[number];

/**
 * Segmentos cujo ciclo normal passa de um dia.
 *
 * Estética fica de fora: o serviço do detailer leva de 4h a 2 dias, então 24h
 * continua sendo o recorte certo para ele. Só funilaria roda em semanas.
 */
const LONG_CYCLE_SEGMENTS: ReadonlySet<string> = new Set(['funilaria']);

export function isStaleOsHoursChoice(value: unknown): value is StaleOsHoursChoice {
  return (
    typeof value === 'number' &&
    (STALE_OS_HOURS_CHOICES as readonly number[]).includes(value)
  );
}

/**
 * O limiar que a oficina recebe sem configurar nada, a partir dos segmentos
 * que ela declarou. Quem não declarou nada é tratado como mecânica — é o que
 * a base inteira já vive hoje, e manter isso é o que garante que nenhuma
 * oficina atual perceba a mudança.
 */
export function defaultStaleOsHours(
  segments: readonly string[] | null | undefined,
): number {
  if (!segments) return STALE_OS_HOURS_DEFAULT;
  return segments.some((s) => LONG_CYCLE_SEGMENTS.has(s))
    ? STALE_OS_HOURS_LONG_CYCLE
    : STALE_OS_HOURS_DEFAULT;
}

/**
 * O limiar efetivo: override do gestor quando válido, senão o default
 * derivado. `override` é tipado como `unknown` porque é exatamente isso que
 * ele é — um campo de jsonb.
 */
export function resolveStaleOsHours(
  override: unknown,
  segments: readonly string[] | null | undefined,
): number {
  return isStaleOsHoursChoice(override) ? override : defaultStaleOsHours(segments);
}

/**
 * Como dizer o limiar para o gestor. "há mais de 96h" ninguém converte de
 * cabeça no balcão — e é justamente a oficina de ciclo longo que leria isso.
 */
export function staleOsHoursLabel(hours: number): string {
  if (hours < 48) return `${hours}h`;
  if (hours % 168 === 0) {
    const weeks = hours / 168;
    return weeks === 1 ? '1 semana' : `${weeks} semanas`;
  }
  if (hours % 24 === 0) return `${hours / 24} dias`;
  return `${hours}h`;
}

/* ==========================================================================
 * Texto da seção "Tipos de serviço" — app do gestor e painel web
 * ========================================================================== */

/** O pedaço de uma linha de módulo que os textos abaixo leem. */
export interface WorkshopModuleHintRow {
  module: string;
  label: string;
  locked: boolean;
  declared: boolean;
  /** Escolha explícita do gestor; `null` = seguindo as especialidades. */
  choice: boolean | null;
}

/**
 * "mecânica 24h, funilaria 4 dias, estética 24h" — cada módulo com o SEU
 * limiar, lido da mesma regra que a API aplica (`defaultStaleOsHours` do
 * segmento). Agrupar "mecânica e estética" à mão passaria a mentir no dia em
 * que um dos dois mudasse.
 */
export function moduleStaleSummary(modules: readonly WorkshopModuleHintRow[]): string {
  return modules
    .map((m) => `${m.label.toLowerCase()} ${staleOsHoursLabel(defaultStaleOsHours([m.module]))}`)
    .join(', ');
}

/** Por que o módulo está como está — a tela diz a causa, não só o estado. */
export function moduleStatusHint(m: WorkshopModuleHintRow): string {
  if (m.locked) return 'Sempre ligado: é o tipo de qualquer serviço que não seja dos outros.';
  if (m.choice === true) return 'Ligado por você.';
  if (m.choice === false) return 'Desligado por você, mesmo com a especialidade marcada.';
  if (m.declared) return 'Ligado pelas especialidades que você marcou.';
  return 'Desligado. Ligue se a oficina também faz este tipo de serviço.';
}
